<script setup lang="ts">
import { api } from '@/api';
import {
  QaRtSystemTemplateContainerDto,
  TaskItemQaEquipmentSetupDto,
  TaskItemQaEquipmentSetupDtoTypeEnum,
  TaskTypeDescription,
} from '@/api/generator';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { NIL } from 'uuid';
import { useField } from 'vee-validate';
import { computed, onMounted, ref, toRef } from 'vue';
import FormAddButton from './FormAddButton.vue';
import QaSectionGroup from './QaSectionGroup.vue';
import QaSectionSystemSelection from './QaSectionSystemSelection.vue';
import { loadQaRtSystemTemplateContainers } from './QaSectionUtil';
import { truthy } from '@/utils/helper';
import { usesSingleQaEquipmentGroup } from './setupParameterRules';

const props = defineProps<{
  name: string; // taskItemAreas[{areaIndex}].qaEquipmentSetups
  taskType: TaskTypeDescription;
  medicalSystemSelected: boolean;
}>();

const mode = useFormMode();

const { value: groupsArray, setValue: setGroupsArray } = useField<TaskItemQaEquipmentSetupDto[]>(
  toRef(() => props.name),
);

function pushGroup(group: TaskItemQaEquipmentSetupDto) {
  setGroupsArray([...groupsArray.value, group]);
}

function removeGroup(index: number) {
  setGroupsArray(groupsArray.value.toSpliced(index, 1));
}

const createNewModalOpen = ref(false);
const isSubmittingQaSelection = ref(false);

const addNewQaSectionGroup = () => {
  createNewModalOpen.value = true;
};

async function handleAddQaSectionGroup(supportSystemEntityIds: string[], groupName: string) {
  if (isSubmittingQaSelection.value) {
    return;
  }

  createNewModalOpen.value = false;
  isSubmittingQaSelection.value = true;

  try {
    const { result } = await api.taskItemsApi.getTaskItemQaEquipmentSetupTemplate({
      getQaEquipmentSetupTemplateDto: {
        supportSystemEntityIds: supportSystemEntityIds,
        taskType: props.taskType,
        previousQaEquipmentSetup: null,
      },
    });

    if (result?.singleSetupParameters) {
      pushGroup({
        id: NIL,
        groupId: result.groupId,
        name: groupName,
        supportSystemEntityIds,
        singleSetupParameters: result.singleSetupParameters,
        $type:
          TaskItemQaEquipmentSetupDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoTaskItemQaEquipmentSetupDtoPtwPtw100AppZeroApplicationShared,
      });
    }
  } finally {
    isSubmittingQaSelection.value = false;
  }
}

// all availible qaRtSystemTemplateContainerDtos
const qaRtSystemTemplateContainerDtos = ref<QaRtSystemTemplateContainerDto[] | undefined | null>(
  undefined,
);

onMounted(() => {
  loadQaRtSystemTemplateContainers(
    props.taskType,
    (result) => (qaRtSystemTemplateContainerDtos.value = result),
    [],
  );
});

// check for items alread used in other groups
const alreadyUsedIDs = computed<string[]>(() => {
  return groupsArray.value.flatMap((group) => group.supportSystemEntityIds).filter(truthy);
});

const alreadyUsedGroupNames = computed<string[]>(() => {
  return groupsArray.value.flatMap((group) => group.name).filter(truthy);
});

const numberOfEquimpemtDtos = computed(() =>
  qaRtSystemTemplateContainerDtos?.value?.reduce(
    (count, item) => count + (item?.entries?.length || 0),
    0,
  ),
);

const showAddGroupButton = computed(() => {
  if (mode.value === 'details') {
    return false;
  }

  const hasTemplates =
    qaRtSystemTemplateContainerDtos.value?.length &&
    qaRtSystemTemplateContainerDtos.value?.length > 0;

  if (usesSingleQaEquipmentGroup(props.taskType)) {
    return alreadyUsedIDs.value.length === 0;
  }

  const hasRemainingIDs = alreadyUsedIDs.value.length < (numberOfEquimpemtDtos.value || 0);
  return hasTemplates && hasRemainingIDs;
});

// Single group task types cannot drop their group. A task saved with several
// groups under an earlier rule keeps the delete button until one is left.
const canRemoveGroup = computed(
  () => !usesSingleQaEquipmentGroup(props.taskType) || groupsArray.value.length > 1,
);

const areSupportSystemsAdded = computed(() => {
  return groupsArray.value.some(
    (setup) => setup.supportSystemEntityIds?.length && setup.supportSystemEntityIds?.length > 0,
  );
});
</script>

<template>
  <v-card :class="$attrs.class">
    <v-card-item>
      <v-card-title>
        <h5>{{ $t('DosimetryAndQaEquipment') }}</h5>
        <VSpacer />
        <FormAddButton
          v-if="showAddGroupButton"
          :disabled="!medicalSystemSelected || isSubmittingQaSelection"
          :label="'SelectSupportSystem'"
          @click="addNewQaSectionGroup"
        />
      </v-card-title>
    </v-card-item>

    <v-card-text class="d-flex flex-column ga-6">
      <!-- corrections only make sense once there is equipment to correct for -->
      <slot v-if="areSupportSystemsAdded" name="corrections" />

      <InlineValidationError ignoreTouched class="inline-error dense mt-1" :name="name" />

      <p
        v-if="!areSupportSystemsAdded"
        class="text-body-1 text-primary-lighten-2 font-weight-semibold"
      >
        {{
          medicalSystemSelected
            ? $t('SelectRequiredDosimetryAndQEquipment')
            : $t('NoDosimetryAndQaEquipmentsAvailable')
        }}
      </p>

      <!-- iterate over groups -->
      <QaSectionGroup
        v-for="(_, index) in groupsArray"
        :key="index"
        :name="`${name}[${index}]`"
        :taskType="taskType"
        :alreadyUsedIDs="alreadyUsedIDs"
        :alreadyUsedGroupNames="alreadyUsedGroupNames"
        :removable="canRemoveGroup"
        class="container-level-2"
        @removeItem="removeGroup(index)"
      />
    </v-card-text>
  </v-card>

  <QaSectionSystemSelection
    v-model="createNewModalOpen"
    :name="name"
    :taskType="taskType"
    :qaRtSystemTemplateContainerDtos="qaRtSystemTemplateContainerDtos || []"
    :alreadyUsedIDs="alreadyUsedIDs"
    :alreadyUsedGroupNames="alreadyUsedGroupNames"
    @submit="handleAddQaSectionGroup"
  />
</template>
<style lang="scss" scoped>
.v-card {
  &.content-block {
    .v-card-text {
      padding: 24px !important;
    }
  }
}
</style>
